// ── 직원 급여 정보 등록/수정 모달 ─────────────────────────────────────────────
// PayrollEmployees.jsx에서 분리 (300줄 제한 준수)

import React from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn } from '../adminlayout/designStandard';
import PayrollProfileHistoryPanel from './PayrollProfileHistoryPanel';
import PayrollEmployeeBankSection from './PayrollEmployeeBankSection';

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)',
  fontSize: 12, background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
const btnPurple = {
  border: 'none', borderRadius: 8, padding: '7px 18px',
  background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: '#fff',
  fontWeight: 700, fontSize: 13, cursor: 'pointer',
};
const btnOutline = {
  border: '1.5px solid var(--border)', borderRadius: 8, padding: '6px 14px',
  background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 600, fontSize: 12, cursor: 'pointer',
};
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  boxSizing: 'border-box',
};
const modalStyle = {
  background: 'var(--bg-card)', borderRadius: 16,
  width: 'min(540px, calc(100vw - 24px))',
  maxWidth: 'calc(100vw - 24px)',
  maxHeight: 'min(90vh, calc(100dvh - 24px))', display: 'flex', flexDirection: 'column',
  boxSizing: 'border-box',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const responsiveTwoCol = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 10, minWidth: 0 };
const sectionBox = { background: 'var(--bg-subtle)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, boxSizing: 'border-box' };

/**
 * 직원 급여 정보 등록/수정 모달
 * @param {object} props
 * @param {number|null} props.editId — null이면 신규 등록
 * @param {object} props.form — 기본 정보 폼 상태
 * @param {function} props.setForm
 * @param {Array} props.itemValues — 고정수당 [{ pay_item_id, item_name, amount }]
 * @param {function} props.setItemValues
 * @param {Array} props.allUsers — 전체 직원 목록
 * @param {Set<number>} props.registeredUserIds — 이미 등록된 직원 ID Set
 * @param {Array} props.bankCodes — 은행코드 활성 목록 [{ id, code, name }]
 * @param {boolean} props.saving
 * @param {function} props.onSave — 저장 핸들러
 * @param {function} props.onClose — 닫기
 */
export default function PayrollEmployeeModal({
  editId, form, setForm, itemValues, setItemValues,
  allUsers = [], registeredUserIds = new Set(), bankCodes = [], saving, onSave, onClose,
  profileHistory = [], onLoadHistory, onDeleteHistory,
  editName = '',
}) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 — 검정 표준 밴드 */}
        <div style={stdModalHeaderBar(16)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>💰</div>
            <span style={stdModalHeaderTitle}>
              {editId ? `${editName ? `${editName} ` : '직원 '}급여 정보 수정` : '직원 급여 정보 등록'}
            </span>
          </div>
          <button onClick={onClose} style={stdModalCloseBtn}>✕</button>
        </div>

        {/* 바디 */}
        <div style={{ padding: 'clamp(12px, 4vw, 20px)', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          {/* 직원 선택 (신규만) — 미등록 직원 위, 등록된 직원 아래(disabled) */}
          {!editId && (() => {
            const available = allUsers.filter((u) => !registeredUserIds.has(Number(u.id)));
            const registered = allUsers.filter((u) => registeredUserIds.has(Number(u.id)));
            return (
              <div>
                <label style={labelStyle}>직원 선택</label>
                <select style={inputStyle} value={form.user_id} onChange={(e) => {
                  const userId = Number(e.target.value);
                  const user = allUsers.find((u) => Number(u.id) === userId);
                  const bankName = user?.bank_name || '';
                  setForm({ ...form, user_id: userId, bank_name: bankName, bank_account: user?.bank_account || '' });
                }}>
                  <option value="">-- 선택 --</option>
                  {available.length > 0 && (
                    <optgroup label="등록 가능">
                      {available.map((u) => (
                        <option key={u.id} value={u.id}>{u.name} ({u.user_id})</option>
                      ))}
                    </optgroup>
                  )}
                  {registered.length > 0 && (
                    <optgroup label="이미 등록됨">
                      {registered.map((u) => (
                        <option key={u.id} value={u.id} disabled style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                          {u.name} ({u.user_id}) (등록됨)
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            );
          })()}

          {/* 기본 정보 */}
          <div style={sectionBox}>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-sub)' }}>기본 정보</div>
            <div style={responsiveTwoCol}>
              <div>
                <label style={labelStyle}>급여 유형</label>
                <select style={inputStyle} value={form.pay_type} onChange={(e) => setForm({ ...form, pay_type: e.target.value })}>
                  <option value="monthly">월급</option>
                  <option value="hourly">시급</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>{form.pay_type === 'monthly' ? '기본급 (월)' : '시급'}</label>
                <input style={inputStyle} value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>부양가족 수 (본인 포함)</label>
                <input style={inputStyle} type="number" min="1" value={form.dependents_count} onChange={(e) => setForm({ ...form, dependents_count: e.target.value })} />
              </div>
            </div>
          </div>

          {/* 4대보험 가입 여부 */}
          <div style={{ ...sectionBox, gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-sub)', marginBottom: 8 }}>4대보험 가입</div>
            <div style={{ display: 'flex', gap: 16 }}>
              {[
                { key: 'national_pension_yn', label: '국민연금' },
                { key: 'health_insurance_yn', label: '건강보험' },
                { key: 'employment_ins_yn', label: '고용보험' },
              ].map(({ key, label }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* 수습 기간 설정 */}
          <div style={sectionBox}>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-sub)' }}>
              수습 기간
              <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 6 }}>0 = 수습 없음 · 입사일부터 계산</span>
            </div>
            <div style={responsiveTwoCol}>
              <div>
                <label style={labelStyle}>수습 기간</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={{ ...inputStyle, flex: 1, minWidth: 0 }} type="text" inputMode="numeric"
                    value={form.probation_months ?? 0}
                    onChange={(e) => setForm({ ...form, probation_months: e.target.value.replace(/[^0-9]/g, '') })}
                    placeholder="0"
                  />
                  <select style={{ ...inputStyle, width: 76, flexShrink: 0 }}
                    value={form.probation_period_unit || 'month'}
                    onChange={(e) => setForm({ ...form, probation_period_unit: e.target.value })}
                  >
                    <option value="month">개월</option>
                    <option value="day">일</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>수습 급여 비율 (%)</label>
                <input style={inputStyle} type="text" inputMode="numeric"
                  value={form.probation_pct ?? 90}
                  onChange={(e) => setForm({ ...form, probation_pct: e.target.value.replace(/[^0-9]/g, '') })}
                  placeholder="90"
                />
              </div>
            </div>
          </div>

          <PayrollEmployeeBankSection form={form} setForm={setForm} bankCodes={bankCodes} />

          {/* 고정 지급 항목 (ON/OFF 토글 + 금액) */}
          {itemValues.length > 0 && (
            <div style={{ ...sectionBox, gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-sub)' }}>
                지급 항목 설정
                <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 6 }}>OFF 항목은 급여·명세서에서 제외</span>
              </div>
              {itemValues.map((v, idx) => {
                const enabled = v.is_enabled !== false;
                return (
                  <div key={v.pay_item_id} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: enabled ? 1 : 0.45, flexWrap: 'wrap' }}>
                    {/* ON/OFF 토글 */}
                    <button
                      type="button"
                      onClick={() => {
                        const next = [...itemValues];
                        next[idx] = { ...next[idx], is_enabled: !enabled };
                        setItemValues(next);
                      }}
                      style={{
                        width: 38, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                        background: enabled ? '#7c3aed' : 'var(--border)', position: 'relative', flexShrink: 0,
                        transition: 'background 0.2s',
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: 2, left: enabled ? 20 : 2,
                        width: 16, height: 16, borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', minWidth: 80 }}>{v.item_name}</span>
                    <input
                      style={{ ...inputStyle, flex: '1 1 140px', minWidth: 0 }}
                      value={v.amount}
                      disabled={!enabled}
                      onChange={(e) => {
                        const next = [...itemValues];
                        next[idx] = { ...next[idx], amount: e.target.value };
                        setItemValues(next);
                      }}
                      placeholder="0"
                    />
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>원</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* 메모 */}
          <div>
            <label style={labelStyle}>메모</label>
            <textarea style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }} value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
          </div>

          {/* 급여정보 적용 + 적용 이력 — 저장 직전(편집 항목 다 채운 뒤 적용일·이력 확인) */}
          <PayrollProfileHistoryPanel
            editId={editId}
            form={form}
            setForm={setForm}
            profileHistory={profileHistory}
            onLoadHistory={onLoadHistory}
            onDeleteHistory={onDeleteHistory}
          />
        </div>

        {/* 푸터 */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--bg-subtle)',
          display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
        }}>
          <button style={btnOutline} onClick={onClose}>취소</button>
          <button style={btnPurple} onClick={onSave} disabled={saving}>
            {saving ? '저장 중...' : editId ? '수정' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
}
